/**
 * Shared utility for WhatsApp redirections and URL generation.
 * Handles both client-side and server-side logic (where applicable).
 */
export const WhatsAppUtils = {
  /**
   * Cleans a phone number to only include digits.
   */
  formatPhone: (phone: string): string => {
    return phone.replace(/\D/g, '');
  },

  /**
   * Generates a standard WhatsApp URL (wa.me).
   * Safe for both Server and Client environments.
   */
  getWhatsAppUrl: (phone: string, message?: string): string => {
    const cleanPhone = WhatsAppUtils.formatPhone(phone);
    const baseUrl = `https://wa.me/${cleanPhone}`;
    if (!message) return baseUrl;
    
    return `${baseUrl}?text=${encodeURIComponent(message)}`;
  },

  /**
   * Performs a client-side redirection to WhatsApp.
   * Uses the 'whatsapp://' protocol for mobile devices with a fallback.
   * 
   * WARNING: This should only be called in a Browser environment.
   */
  redirectToWhatsApp: (phone: string, message?: string): void => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      console.warn('redirectToWhatsApp called outside of a browser environment.');
      return;
    }

    const cleanPhone = WhatsAppUtils.formatPhone(phone);
    const encodedMsg = message ? encodeURIComponent(message) : '';
    
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    if (isMobile) {
      // Try the direct protocol first - it's the fastest way to jump into the app
      const protocolUrl = `whatsapp://send?phone=${cleanPhone}${encodedMsg ? `&text=${encodedMsg}` : ''}`;
      window.location.href = protocolUrl;

      // Fallback for mobile if the protocol fails (e.g., app not installed)
      setTimeout(() => {
        window.location.href = WhatsAppUtils.getWhatsAppUrl(phone, message);
      }, 500);
    } else {
      // Desktop: Use the API URL in a new tab
      window.open(WhatsAppUtils.getWhatsAppUrl(phone, message), '_blank');
    }
  },

  /**
   * Redirects to the official contact/support page (tufirma.pro/contacto).
   * Supports an optional prefilled message.
   */
  redirectToContact: (message?: string): void => {
    const baseUrl = 'https://tufirma.pro/contacto';
    const url = message ? `${baseUrl}?message=${encodeURIComponent(message)}` : baseUrl;
    
    if (typeof window !== 'undefined') {
      window.open(url, '_blank');
    }
  },

  /**
   * Predefined messages for different landing page entry points.
   */
  MESSAGES: {
    GENERAL: "Hola, me gustaría recibir más información sobre tufirma.pro",
    FREE_TRIAL: "¡Hola! Quiero empezar mis 7 días de prueba gratuita en tufirma.pro. ¿Cómo podemos iniciar?",
  },
};
